/**
 * Client-side data access for the business/sell lead application submission.
 * Imperative `$fetch` via `useApi()` targeting the BFF, which proxies the
 * upstream v2 endpoint.
 */
export const useLeadApplicationSubmissionRepository = () => {
  const api = useApi()
  const { getToken } = useTurnstile()

  // Both calls reach the Hub's captcha check, and each token is single-use.
  const submit = async (body: LeadApplicationSubmitBody) =>
    api<LeadApplicationSubmitResponse>(
      '/lead-application/v2/lead-applications',
      { method: 'POST', body, headers: captchaHeaders(await getToken()) }
    )

  // Precognitive email-only validation on the same route. Plain `$fetch.raw`
  // (not `useApi`) so non-2xx answers resolve with their status instead of
  // throwing and being logged as errors.
  const checkEmailAvailability = async (
    email: string
  ): Promise<TEmailAvailabilityResponse> => {
    const response = await $fetch.raw(
      '/api/lead-application/v2/lead-applications',
      {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Precognition: 'true',
          'Precognition-Validate-Only': 'email',
          ...captchaHeaders(await getToken()),
        },
        body: { email },
        ignoreResponseError: true,
      }
    )

    return { status: response.status, data: response._data }
  }

  // Company enrichment for an email domain (AP-11092). The BFF always answers
  // 202 and says whether enrichment started; plain `$fetch` (not `useApi`) so
  // nothing is logged.
  const requestCompanyEnrichment = (domain: string) =>
    $fetch<TCompanyEnrichmentResponse>('/api/lead-application/v2/enrichment', {
      method: 'POST',
      body: { domain },
    })

  return { submit, checkEmailAvailability, requestCompanyEnrichment }
}
